import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const SosAlertModal = ({ open, data, onRespond, onBlock, onShowLocation }) => {
  const order = data?.order;
  const sos = data?.sos;
  const driverContact =
    order?.driver?.contact ||
    order?.driver_contact ||
    sos?.driver?.contact ||
    sos?.driver_contact ||
    "-";
  const latitude = sos?.latitude ?? sos?.location?.latitude;
  const longitude = sos?.longitude ?? sos?.location?.longitude;
  const hasLocation = Boolean(latitude && longitude);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 680,
          maxHeight: "90vh",
          borderRadius: 3,
          border: "2px solid #d32f2f",
          boxShadow: "0 16px 48px rgba(211, 47, 47, 0.35)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #b71c1c 0%, #e53935 100%)",
          color: "#fff",
          p: 3,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.9 }}>
              SOS ALERT
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
              Immediate Attention Required
            </Typography>
          </Box>
          <Chip
            label={sos?.status ? sos.status.toUpperCase() : "ACTIVE"}
            sx={{
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#fff",
              fontWeight: 700,
            }}
          />
        </Stack>
      </Box>

      <Box sx={{ p: 3, maxHeight: "55vh", overflowY: "auto" }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Order Ref
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {order?.order_ref || order?.id || "-"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Category
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {order?.category?.name || order?.express_category?.name || "-"}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary">
              Dropoff Address
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {order?.dropoff_address || "-"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Order Status
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {order?.current_order_status?.name || "-"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Driver Contact
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {driverContact}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Note
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {sos?.note  || "-"}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                SOS Location
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {latitude && longitude ? `${latitude}, ${longitude}` : "-"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Reported At
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatDateTime(sos?.createdAt || order?.createdAt)}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3,
          pb: 3,
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Button
          fullWidth
          variant="contained"
          color="error"
          onClick={onShowLocation}
          disabled={!hasLocation}
          sx={{
            py: 1.2,
            fontWeight: 700,
            letterSpacing: 0.4,
          }}
        >
          Show Location
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="error"
          onClick={onRespond}
          sx={{
            py: 1.2,
            fontWeight: 700,
            letterSpacing: 0.4,
          }}
        >
          Respond
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={onBlock}
          sx={{
            py: 1.2,
            fontWeight: 700,
            letterSpacing: 0.4,
            bgcolor: "#424242",
            color: "#fff",
            "&:hover": { bgcolor: "#2f2f2f" },
          }}
        >
          Block
        </Button>
      </Box>
    </Dialog>
  );
};

SosAlertModal.propTypes = {
  open: PropTypes.bool.isRequired,
  data: PropTypes.object,
  onRespond: PropTypes.func.isRequired,
  onBlock: PropTypes.func.isRequired,
  onShowLocation: PropTypes.func.isRequired,
};

SosAlertModal.defaultProps = {
  data: null,
};

export default SosAlertModal;
